import {
  Component,
  forwardRef,
  OnInit,
  Input,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  ValidationErrors,
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import * as cardValidator from 'card-validator';

import {
  formatCardNumber,
  formatCVC,
  formatExpiry,
} from 'src/app/core/utility/common.utils';

@Component({
  selector: 'app-card-input',
  templateUrl: './card-input.component.html',
  styleUrls: ['./card-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CardInputComponent),
      multi: true,
    },
    {
      /**
       * This addition allows the component
       * to also act as a custom validator.
       */
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CardInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardInputComponent
  implements OnInit, ControlValueAccessor, Validator
{
  @Input() showCvc: boolean = true;
  @Input() marginless: boolean = false;
  @Input() collectZip: boolean = false;

  get expirationColumnClass(): string {
    if (this.collectZip && this.showCvc) {
      return 'col-4';
    }
    if (this.collectZip || this.showCvc) {
      return this.collectZip ? 'col-8' : 'col-sm-8';
    }
    return 'col-sm-12';
  }

  form: FormGroup;
  maxLengthCard: number | null = null;
  maxLengthCvc: number | null = null;
  gaps: number[] | null = [4, 8, 12];

  cardType: string;

  // ControlValueAccessor callbacks
  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    const cvcValidators = this.showCvc
      ? [Validators.required, Validators.minLength(3), Validators.maxLength(4)]
      : [];
    const postalValidators = this.collectZip
      ? [CardInputComponent.postalCodeValidator]
      : [];

    this.form = this.fb.group({
      cardNumber: ['', [Validators.required, this.cardNumberValidator]],
      expirationDate: ['', [Validators.required, this.expirationDateValidator]],
      cvc: ['', cvcValidators],
      postalCode: ['', postalValidators],
      type: [''],
      expirationYear: [''],
      expirationMonth: [''],
    });

    // Update parent form whenever local form value changes
    // this.form.valueChanges.subscribe((value) => {
    //   this.onChangeFn(value);
    // });

    this.form.get('cvc')?.valueChanges.subscribe((value) => {
      this.updateCvcValue(value);
      this.onChangeFn(this.getFormData());
    });

    this.form.get('expirationDate')?.valueChanges.subscribe((value) => {
      this.updateExpiryDate(value);

      const shortValue = formatExpiry(value);
      this.form
        .get('expirationDate')
        ?.setValue(shortValue, { emitEvent: false });
      this.onChangeFn(this.getFormData());
    });

    this.form.get('postalCode')?.valueChanges.subscribe(() => {
      this.onChangeFn(this.getFormData());
    });

    this.form.get('cardNumber')?.valueChanges.subscribe((value) => {
      this.updateCardTypeIcon(value);

      const shortValue = formatCardNumber(value, this.gaps as number[]).slice(
        0,
        this.maxLengthCard || value.length
      );
      this.form.get('cardNumber')?.setValue(shortValue, { emitEvent: false });
      this.updateCvcValue(this.form.value.cvc);
      this.onChangeFn(this.getFormData());
    });
  }

  updateCvcValue(value: string) {
    const formattedValue = formatCVC(value);
    const shortValue = formattedValue.slice(
      0,
      this.maxLengthCvc || formattedValue.length
    );

    this.form.get('cvc')?.setValue(shortValue, { emitEvent: false });
  }

  getFormData() {
    const formData = this.form.value;
    return {
      cardNumber: formData.cardNumber?.replace(/\s/g, ''),
      cvc: formData.cvc,
      type: formData.type,
      expirationMonth: formData.expirationMonth,
      expirationYear: formData.expirationYear,
      postalCode: `${formData.postalCode ?? ''}`.trim(),
    };
  }

  /**
   * Required length is measured after trim, so spaces alone do not count.
   */
  static postalCodeValidator(control: AbstractControl): ValidationErrors | null {
    const value = `${control.value ?? ''}`.trim();
    if (!value) {
      return {required: true};
    }
    if (value.length < 3) {
      return {minlength: {requiredLength: 3, actualLength: value.length}};
    }
    if (value.length > 10) {
      return {maxlength: {requiredLength: 10, actualLength: value.length}};
    }
    return null;
  }

  /**
   * Custom validator for card number using "card-validator"
   */
  cardNumberValidator = (control: any) => {
    if (!control.value) return null;
    const validation = cardValidator.number(control.value);
    if (
      !validation.isPotentiallyValid ||
      (validation.card && !validation.isValid)
    ) {
      return { invalidCardNumber: true };
    }
    return null;
  };

  /**
   * Custom validator for expiration date using "card-validator"
   *
   * Expects date in "MM/YY" or "MM/YYYY" format.
   */
  expirationDateValidator = (control: any) => {
    if (!control.value) return null;
    const validation = cardValidator.expirationDate(control.value);
    if (!validation.isValid) {
      return { invalidExpirationDate: true };
    }
    return null;
  };

  /**
   * Dynamically update the card-type icon based on the card number.
   */
  private updateCardTypeIcon(cardNumber: string) {
    const validation = cardValidator.number(cardNumber.replace(/\D+/g, ''));
    if (validation.card && validation.card.type) {
      this.updateGaps(validation.card.gaps);
      const maxAllowableLength = Math.max(...validation.card.lengths);
      this.maxLengthCard = maxAllowableLength + (this.gaps?.length || 0);
      this.maxLengthCvc = validation.card.code.size;
      const type = validation.card.type;
      this.cardType = type;
      this.form.get('type')?.setValue(type);
    } else {
      this.cardType = 'generic';
      this.form.get('type')?.setValue('generic');
      this.maxLengthCard = null;
      this.maxLengthCvc = null;
      this.updateGaps([4, 8, 12]);
    }
  }

  updateGaps(newGaps: number[]) {
    if (this.gaps?.length && this.gaps.length === newGaps.length) {
      const isSameGaps = this.gaps.every(
        (value, index) => newGaps[index] === value
      );
      if (isSameGaps) {
        return;
      }
    }
    this.gaps = newGaps;
  }

  /**
   * Dynamically update the card-type icon based on the card number.
   */
  private updateExpiryDate(expiry: string) {
    const validation = cardValidator.expirationDate(expiry);
    if (validation.isValid) {
      this.form.get('expirationMonth')?.setValue(validation.month);
      this.form.get('expirationYear')?.setValue(validation.year);
    } else {
      this.form.get('expirationMonth')?.setValue(null);
      this.form.get('expirationYear')?.setValue(null);
    }
  }

  // --------------------------------------------------
  // ControlValueAccessor Methods
  // --------------------------------------------------
  writeValue(value: any): void {
    if (value) {
      this.form.setValue(value, { emitEvent: false });
      this.updateCardTypeIcon(value.cardNumber);
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  handleOnTouched() {
    if (this.onTouchedFn) {
      this.onTouchedFn();
    }
  }

  /**
   * Browser autocomplete writes the ZIP into the input without an `input`
   * event, so the form control stays empty and the parent never hears that
   * the card became valid. `change` covers a committed autofill; the
   * autofill animation covers Chrome, which often skips both events.
   * Emulated encapsulation prefixes the keyframe, so the reported name is
   * like `_ngcontent-ng-c0_onAutoFillStart` rather than `onAutoFillStart`.
   */
  syncPostalCode(event: Event): void {
    if (
      event.type === 'animationstart' &&
      !(event as AnimationEvent).animationName?.includes('onAutoFillStart')
    ) {
      return;
    }
    this.applyPostalCode(event);
    if (event.type === 'animationstart') {
      setTimeout(() => this.applyPostalCode(event));
    }
  }

  private applyPostalCode(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }
    const value = input.value;
    const control = this.form?.get('postalCode');
    if (!control || control.value === value) {
      return;
    }
    control.setValue(value);
  }

  // --------------------------------------------------
  // Validator Interface Implementation
  // --------------------------------------------------
  validate(control: AbstractControl): ValidationErrors | null {
    // Return this component's form errors if any of its fields are invalid.
    // If form is valid, return null (no errors).
    return this.form.valid ? null : { invalidCardInput: this.form.errors };
  }
}
